import React, { useState, useEffect, FC, useCallback, useMemo } from 'react'
import { Tree } from 'antd'
import type { TreeDataNode, TreeProps } from 'antd'
import { produce } from 'immer'
import { TodayIconFont as IconFont } from '../components/IconFonts'
import { Department } from '../../global'
import sensors from '../../utils/sensors'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import { setParentDepartmentIDs } from '../../slices/organizationSlice'
import { CaretDownOutlined } from '@ant-design/icons'

interface FindCurDepartment {
  (data: Department): Department | null
}

interface IProps {
  onCurDepartmentChange: (department: Department | null) => void
}

const DepartmentTree: FC<IProps> = (props) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])

  const organization = useAppSelector(
    (state) => state.organization.organization
  )
  const curDepartment = useAppSelector(
    (state) => state.organization.curDepartment
  )
  const groupID: string = useAppSelector((state) => state.group.groupId)
  const dispatch = useAppDispatch()

  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  // 生成组织架构树数据 (antd 使用 treeData 属性)
  const generateTreeData = useCallback(
    (data: Department | Department[] | null): TreeDataNode[] => {
      if (!data) return []

      const processNode = (department: Department): TreeDataNode => {
        const icon = (
          <IconFont
            type="icondepartment"
            style={{
              color: selectedKeys.includes(department.id)
                ? '#FFFFFF'
                : '#0093FF',
              fontSize: '20px'
            }}
          />
        )
        return {
          key: department.id,
          title: department.name + `（${department.num}）`,
          icon,
          children:
            department.children.length > 0
              ? department.children.map(processNode)
              : undefined
        }
      }

      if (Array.isArray(data)) {
        return data.map(processNode)
      } else {
        return [processNode(data)]
      }
    },
    [selectedKeys]
  )

  // 使用 useMemo 缓存 treeData
  const treeData = useMemo(
    () => generateTreeData(organization),
    [organization, generateTreeData]
  )

  /**
   *
   * @param data
   * @returns
   * 根据选择的部门ID，查找当前选中的部门
   * 同时记录所有父级部门
   */
  const findCurDepartment: FindCurDepartment = (data) => {
    let cur: Department | null = null //当前选中的部门
    let parentDepartmentIDs: string[] = [] //当前部门的所有父级部门

    interface IFindCurDepartmentRecursive {
      (data: Department[]): boolean
    }

    const findCurDepartmentRecursive: IFindCurDepartmentRecursive = (
      departments
    ) => {
      for (let index = 0; index < departments.length; index++) {
        const element = departments[index]
        parentDepartmentIDs.push(element.id)
        if (selectedKeys.includes(element.id)) {
          cur = element
          return true
        }
        if (element.children.length > 0) {
          if (findCurDepartmentRecursive(element.children)) {
            return true
          }
        }
        parentDepartmentIDs.pop()
      }
      return false
    }

    if (selectedKeys[0] === '0') {
      dispatch(setParentDepartmentIDs(['0']))
      return data
    } else if (data) {
      parentDepartmentIDs.push('0')
      findCurDepartmentRecursive(data.children)
    }
    dispatch(setParentDepartmentIDs(parentDepartmentIDs))
    return cur
  }

  const onSelect: TreeProps['onSelect'] = (keys, info) => {
    if (info.selected) {
      //选择一个节点时，如果是选中，则设置为当前部门，并展开此部门
      sensors.track('web_click_page_workgroup_groupcontacts', {
        clickItem: 'listDepartment',
        groupID: groupID
      })
      const selectedKeys = keys as string[]
      setSelectedKeys(selectedKeys)
      const newExpandedKeys = produce(expandedKeys, (draft) => {
        if (draft.indexOf(selectedKeys[0]) < 0) {
          draft.push(selectedKeys[0])
        }
      })
      setExpandedKeys(newExpandedKeys)
    }
  }

  const onExpand: TreeProps['onExpand'] = (keys) =>
    setExpandedKeys(keys as string[])

  //默认选中并展开团队
  useEffect(() => {
    setExpandedKeys(['0'])
    setSelectedKeys(['0'])
  }, [])

  //当选择一个部门时或者组织架构更新时，更新当前选中的部门
  useEffect(() => {
    let curDepartment: Department | null = findCurDepartment(organization)
    props.onCurDepartmentChange(curDepartment)
  }, [selectedKeys, organization])

  //当前部门改变时，选中对应的部门节点（例：删除当前部门后，将当前部门设置为团队）
  useEffect(() => {
    if (curDepartment) {
      setSelectedKeys([curDepartment.id])
    }
  }, [curDepartment])

  return (
    <div className="department-tree-container">
      <Tree
        showIcon
        blockNode
        multiple={false}
        selectedKeys={selectedKeys}
        onSelect={onSelect}
        expandedKeys={expandedKeys}
        onExpand={onExpand}
        switcherIcon={<CaretDownOutlined style={{ fontSize: '20px' }} />}
        treeData={treeData}
      />
    </div>
  )
}

export default DepartmentTree
